/**
 * @license
 * Copyright 2023 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Type-only: this file names `Aglyn.NodeSchema` and nothing else, and a
// VALUE namespace import of the core barrel is opaque to a bundler — it
// cannot know which exports are read, so every module the barrel reaches
// is pinned into the published page's first load.
import type * as Aglyn from '@aglyn/aglyn'
import { observer } from 'mobx-react-lite'
import { forwardRef, useEffect, useMemo } from 'react'
import { DeferredHydrationContext } from '../contexts/deferred-hydration'
import RendererComponents, {
  type RenderComponentsContext,
} from '../contexts/renderer-components'
import Branch from './branch'
import { markTreeHydrated } from './deferred-hydration'
import Leaf from './leaf'
import Stem from './stem'
import Trunk from './trunk'

export interface TreeRootProps extends Partial<RenderComponentsContext> {
  node: Aglyn.NodeSchema
  /**
   * Keep static subtrees' server HTML through hydration and render them
   * when the visitor nears them (AGL-3581). Only for a tree that arrived as
   * server HTML — the published page. See `deferred-hydration.tsx`.
   */
  deferHydration?: boolean
}

export const TreeRoot = observer(
  forwardRef<any, TreeRootProps>((props, ref) => {
    const {
      node,
      TrunkComponent,
      StemComponent,
      BranchComponent,
      LeafComponent,
      deferHydration,
    } = props

    const Components = useMemo<RenderComponentsContext>(
      () => ({
        TrunkComponent: (TrunkComponent ?? Trunk) as RenderComponentsContext['TrunkComponent'],
        StemComponent: (StemComponent ?? Stem) as RenderComponentsContext['StemComponent'],
        BranchComponent: (BranchComponent ?? Branch) as RenderComponentsContext['BranchComponent'],
        LeafComponent: (LeafComponent ?? Leaf) as RenderComponentsContext['LeafComponent'],
      }),
      [TrunkComponent, StemComponent, BranchComponent, LeafComponent],
    )

    // After the first commit — which is the hydration — no leaf mounting
    // later may hold server HTML it never had (AGL-3581).
    useEffect(() => {
      if (deferHydration) markTreeHydrated()
    }, [deferHydration])

    return (
      <DeferredHydrationContext.Provider value={Boolean(deferHydration)}>
        <RendererComponents.Provider value={Components}>
          <Components.TrunkComponent ref={ref} node={node} />
        </RendererComponents.Provider>
      </DeferredHydrationContext.Provider>
    )
  }),
)
TreeRoot.displayName = 'TreeRoot'
TreeRoot['aglyn'] = true

export default TreeRoot
